import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { TabBar } from './TabBar';
import { preloadScreens } from './lazyScreen';
import { LoadingState } from '../ui';
import { useServices } from './services';
import { loadQuickAddBasics } from '../features/quick-add/quickCategories';
import { AdBanner } from '../ads/AdBanner';
import { screenTitleKey, useScreenLocation, useTabScrollMemory } from './navigation';

/**
 * Keeps the document title in step with the screen and tells screen-reader
 * users where a tab switch took them (the page itself does not reload, so
 * nothing else would announce it).
 */
function useScreenAnnouncement(pathname: string): string {
  const { t } = useTranslation();
  const [announcement, setAnnouncement] = useState('');
  const first = useRef(true);
  const titleKey = screenTitleKey(pathname);
  const title = titleKey ? t(titleKey) : '';

  useEffect(() => {
    if (!title) return;
    document.title = `${title} · Costline`;
    if (first.current) {
      first.current = false;
      return;
    }
    setAnnouncement(title);
  }, [title]);

  return announcement;
}

/** Is the keyboard busy typing somewhere (a field, an editable area)? Then letters are letters. */
function typingIn(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return Boolean(el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)));
}

/**
 * Keyboard shortcuts for the web and desktop (no effect while typing or
 * with a sheet open): N or + adds an expense, / searches entries.
 */
function useShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();
  const latest = useRef(location);
  latest.current = location;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || typingIn(e.target)) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === 'n' || e.key === 'N' || e.key === '+') {
        e.preventDefault();
        navigate('/add', { state: { background: latest.current } });
      } else if (e.key === '/') {
        e.preventDefault();
        navigate('/entries', { state: { focusSearch: true } });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
}

export function AppShell() {
  const { t } = useTranslation();
  const screen = useScreenLocation();
  useTabScrollMemory(screen.pathname);
  const announcement = useScreenAnnouncement(screen.pathname);
  useShortcuts();
  // Once the first screen is up and the device is idle, fetch the others: a tab switch never waits,
  // and the start (Home's own data) never competes with it. Safari has no requestIdleCallback: a delay instead.
  useEffect(() => {
    const preload = () => void preloadScreens();
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const id = window.setTimeout(() => (idle ? idle(preload, { timeout: 5000 }) : preload()), 1500);
    return () => window.clearTimeout(id);
  }, []);
  const { store, activeSpace, identity, dataVersion } = useServices();
  // Keep Quick Add warm: its grid is ready before + is tapped, and follows every change.
  useEffect(() => {
    if (activeSpace) void loadQuickAddBasics(store, activeSpace.id, identity.session.userId);
  }, [store, activeSpace, identity, dataVersion]);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('nav.skip')}
      </a>
      <main id="main" className="shell-main" tabIndex={-1}>
        <Suspense fallback={<LoadingState rows={4} />}>
          <Outlet />
        </Suspense>
      </main>
      <TabBar />
      <AdBanner />
      <p className="visually-hidden" role="status" aria-live="polite" data-testid="route-announcer">
        {announcement}
      </p>
    </div>
  );
}
